import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useConcierge } from "@/hooks/useConcierge";
import { ProjectSwitcher } from "@/components/chat/ProjectSwitcher";
import { WidgetRenderer } from "@/components/widgets/WidgetRenderer";
import { ArrowUp, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

interface WidgetAction {
  id: string;
  label: string;
  style?: string;
}

interface HomeMessage {
  id: string;
  role: "user" | "assistant" | "widget";
  content: string;
  widgetType?: string;
  widgetData?: Record<string, unknown>;
  widgetActions?: WidgetAction[];
}

export default function Home() {
  const navigate = useNavigate();
  const [messages, setMessages] = useState<HomeMessage[]>([]);
  const [input, setInput] = useState("");
  const [isBusy, setIsBusy] = useState(false);
  const [transitioning, setTransitioning] = useState<string | null>(null);

  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const handleMessage = useCallback((msg: { type: string; content?: string; project_id?: string; project_name?: string; forward_prompt?: string; widget_type?: string; data?: Record<string, unknown>; actions?: WidgetAction[] }) => {
    switch (msg.type) {
      case "greeting": {
        setMessages([{
          id: crypto.randomUUID(),
          role: "assistant",
          content: msg.content ?? "",
        }]);
        break;
      }
      case "text_delta": {
        setIsBusy(false);
        setMessages((prev) => [
          ...prev,
          {
            id: crypto.randomUUID(),
            role: "assistant",
            content: msg.content ?? "",
          },
        ]);
        break;
      }
      case "text_done": {
        setIsBusy(false);
        break;
      }
      case "widget": {
        setIsBusy(false);
        setMessages((prev) => [
          ...prev,
          {
            id: crypto.randomUUID(),
            role: "widget",
            content: "",
            widgetType: msg.widget_type,
            widgetData: msg.data,
            widgetActions: msg.actions,
          },
        ]);
        break;
      }
      case "route_to_project": {
        // Transition immediately — no response to read
        setTransitioning(msg.project_id ?? null);
        setTimeout(() => {
          navigate(`/chat/${msg.project_id}`, {
            state: { forwardPrompt: msg.forward_prompt },
          });
        }, 400); // Just enough for the dissolve to start
        break;
      }
      case "error": {
        setIsBusy(false);
        setMessages((prev) => [
          ...prev,
          { id: crypto.randomUUID(), role: "assistant", content: "Something went wrong. Try again?" },
        ]);
        break;
      }
    }
  }, [navigate]);

  const { state: wsState, connect, send } = useConcierge({
    onMessage: handleMessage,
  });

  useEffect(() => {
    if (wsState === "disconnected") {
      connect();
    }
  }, [wsState, connect]);

  // Detect OAuth callback — ?github=connected
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("github") === "connected") {
      window.history.replaceState({}, "", "/");
      api.github.status().then((status) => {
        if (status.connected) {
          setMessages((prev) => [
            ...prev,
            {
              id: crypto.randomUUID(),
              role: "widget",
              content: "",
              widgetType: "github_status",
              widgetData: {
                installations: status.installations,
                just_connected: true,
              },
            },
          ]);
        }
      }).catch(() => {
        // Silently fail — user will see status in dropdown
      });
    }
  }, []);

  // Auto-scroll
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
    }
  }, [messages]);

  function handleSend() {
    if (isBusy) return;
    const text = input.trim();
    if (!text) return;

    setInput("");
    setIsBusy(true);

    if (inputRef.current) {
      inputRef.current.style.height = "auto";
    }

    setMessages((prev) => [
      ...prev,
      { id: crypto.randomUUID(), role: "user", content: text },
    ]);

    send(text);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  function handleInputChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
    setInput(e.target.value);
    const el = e.target;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, window.innerHeight * 0.35) + "px";
  }

  const hasInput = input.trim().length > 0;

  return (
    <div className={`flex h-full flex-col bg-intri-bg transition-all duration-slow ${
      transitioning ? "opacity-0 scale-[1.02]" : "opacity-100 scale-100"
    }`}>
      {/* Header */}
      <div className="h-3 sm:h-1 shrink-0 safe-top" />
      <header className="flex items-center gap-3 px-5 sm:px-6 py-3">
        <ProjectSwitcher currentProject={null} />
      </header>

      {/* Chat area */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-4 sm:px-5 pt-4 pb-6"
      >
        <div className="max-w-xl mx-auto space-y-5">

          {/* Loading state */}
          {wsState !== "connected" && messages.length === 0 && (
            <div className="flex h-full items-center justify-center min-h-[200px]">
              <Loader2 size={16} className="text-intri-muted animate-spin" />
            </div>
          )}

          {messages.map((msg) => (
            <div
              key={msg.id}
              className={`animate-slide-up ${
                msg.role === "user" ? "flex flex-col items-end" : ""
              }`}
            >
              {msg.role === "widget" ? (
                <WidgetRenderer
                  widgetType={msg.widgetType!}
                  data={msg.widgetData ?? {}}
                  actions={msg.widgetActions}
                />
              ) : msg.role === "user" ? (
                <div className="max-w-[88%] sm:max-w-[75%] rounded-3xl rounded-br-lg bg-[var(--bubble-user)] px-4 py-3 text-sm leading-relaxed text-intri-text">
                  {msg.content}
                </div>
              ) : (
                <div className="max-w-full sm:max-w-[90%] rounded-3xl rounded-bl-lg bg-[var(--bubble-assistant)] px-4 py-3 text-sm leading-relaxed text-intri-text/[0.9]">
                  {msg.content}
                </div>
              )}
            </div>
          ))}

          {/* Typing indicator */}
          {isBusy && (
            <div className="flex items-center gap-1.5 px-1 py-2 animate-fade-in">
              <span className="typing-dot" />
              <span className="typing-dot [animation-delay:150ms]" />
              <span className="typing-dot [animation-delay:300ms]" />
            </div>
          )}
        </div>
      </div>

      {/* Input */}
      <div className="px-4 sm:px-5 pt-2 pb-4 safe-bottom">
        <div className="max-w-xl mx-auto">
          <div className="flex items-end gap-2 bg-intri-surface/80 rounded-2xl px-3 shadow-intri-md transition-shadow duration-normal focus-within:shadow-intri-glow">
            <textarea
              ref={inputRef}
              value={input}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder={wsState === "connected" ? "Message..." : "Connecting..."}
              disabled={wsState !== "connected"}
              rows={1}
              className="flex-1 resize-none bg-transparent py-3 text-sm text-intri-text placeholder:text-intri-muted/50 focus:outline-none disabled:opacity-40"
            />
            <button
              onClick={handleSend}
              disabled={!hasInput || wsState !== "connected"}
              className={`shrink-0 mb-2 p-1.5 rounded-full transition-all duration-normal ${
                hasInput
                  ? "text-intri-bg hover:brightness-110 scale-100"
                  : "text-intri-muted/40 scale-95"
              } disabled:opacity-30 disabled:scale-95`}
              style={hasInput ? { background: "var(--brand-gradient)" } : undefined}
              title="Send"
            >
              <ArrowUp size={16} strokeWidth={2.5} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
